import { useCallback, useMemo, useState, useEffect } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  ConnectionMode,
  MarkerType,
  Handle,
  Position,
  NodeProps,
  BackgroundVariant,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { 
  Building, 
  Users, 
  TrendingUp, 
  DollarSign, 
  ChevronDown, 
  ChevronRight,
  Phone,
  Mail,
  MapPin,
  Globe,
  Calendar,
  Star,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { useOrganizationHierarchy } from '@/hooks/useOrganizations';
import { useNavigate } from 'react-router-dom';
import type { Organization } from '@/types/organization.types';

interface OrganizationNodeData {
  organization: Organization;
  isExpanded: boolean;
  level: number;
  childCount: number;
  opportunityCount?: number;
  contactCount?: number;
  totalRevenue?: number;
}

const OrganizationNode = ({ data, isConnectable }: NodeProps<OrganizationNodeData>) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const { organization, isExpanded, childCount, opportunityCount = 0, contactCount = 0, totalRevenue = 0 } = data;

  const handleClick = useCallback(() => {
    navigate(`/organizations/${organization.id}`);
  }, [navigate, organization.id]);

  const getHealthColor = (score?: number) => {
    if (!score) return 'text-gray-500';
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getTierColor = (tier?: string) => {
    switch (tier?.toLowerCase()) {
      case 'tier 1':
      case 'strategic':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'tier 2':
      case 'growth':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'tier 3':
      case 'standard':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      default:
        return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  return (
    <div
      className={cn(
        "px-4 py-3 shadow-lg rounded-lg border-2 bg-white transition-all duration-200 cursor-pointer",
        "hover:shadow-xl hover:border-primary/50",
        isHovered && "scale-105"
      )}
      style={{ minWidth: 280, maxWidth: 320 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      <Handle 
        type="target" 
        position={Position.Top} 
        isConnectable={isConnectable}
        className="w-3 h-3 !bg-primary/50 !border-2 !border-primary"
      />
      
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <h3 className="font-semibold text-sm line-clamp-2">{organization.name}</h3>
            {organization.client_type && (
              <p className="text-xs text-muted-foreground mt-0.5">{organization.client_type}</p>
            )}
          </div>
          <Building className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-1.5">
          {organization.tier_ranking && (
            <Badge variant="outline" className={cn("text-xs", getTierColor(organization.tier_ranking))}>
              {organization.tier_ranking}
            </Badge>
          )}
          {organization.health_score && (
            <Badge variant="outline" className="text-xs">
              <span className={cn("mr-1", getHealthColor(organization.health_score))}>●</span>
              Health: {organization.health_score}%
            </Badge>
          )}
          {organization.is_active && (
            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
              Active
            </Badge>
          )}
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="text-center">
            <div className="font-medium text-muted-foreground">Contacts</div>
            <div className="font-semibold flex items-center justify-center gap-1">
              <Users className="h-3 w-3" />
              {contactCount}
            </div>
          </div>
          <div className="text-center">
            <div className="font-medium text-muted-foreground">Opps</div>
            <div className="font-semibold flex items-center justify-center gap-1">
              <TrendingUp className="h-3 w-3" />
              {opportunityCount}
            </div>
          </div>
          <div className="text-center">
            <div className="font-medium text-muted-foreground">Revenue</div>
            <div className="font-semibold flex items-center justify-center gap-1">
              <DollarSign className="h-3 w-3" />
              {totalRevenue > 0 ? `${(totalRevenue / 1000).toFixed(0)}k` : '0'}
            </div>
          </div>
        </div>

        {/* Child indicator */}
        {childCount > 0 && (
          <>
            <Separator className="my-2" />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{childCount} subsidiary{childCount !== 1 ? 'ies' : ''}</span>
              {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            </div>
          </>
        )}
      </div>

      <Handle 
        type="source" 
        position={Position.Bottom} 
        isConnectable={isConnectable}
        className="w-3 h-3 !bg-primary/50 !border-2 !border-primary"
      />
    </div>
  );
};

const nodeTypes = {
  organization: OrganizationNode,
};

interface VisualOrganizationHierarchyProps {
  organizationId?: string;
  className?: string;
}

function VisualOrganizationHierarchyContent({ 
  organizationId, 
  className 
}: VisualOrganizationHierarchyProps) {
  const { data: hierarchyData, isLoading, error } = useOrganizationHierarchy(organizationId);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Convert hierarchy data to nodes and edges
  useEffect(() => {
    if (!hierarchyData) return;

    const newNodes: Node<OrganizationNodeData>[] = [];
    const newEdges: Edge[] = [];
    const nodePositions = new Map<string, { x: number; y: number }>();

    // Helper function to calculate tree layout
    const calculatePosition = (
      org: any,
      level: number = 0,
      index: number = 0,
      parentX: number = 0
    ): number => {
      const horizontalSpacing = 400;
      const verticalSpacing = 200;
      const childrenCount = org.children?.length || 0;
      const totalWidth = childrenCount * horizontalSpacing;
      const startX = parentX - totalWidth / 2 + horizontalSpacing / 2;

      const x = level === 0 ? 0 : startX + index * horizontalSpacing;
      const y = level * verticalSpacing;

      nodePositions.set(org.id, { x, y });

      // Process children if expanded
      if (expandedNodes.has(org.id) && org.children) {
        org.children.forEach((child: any, idx: number) => {
          calculatePosition(child, level + 1, idx, x);
        });
      }

      return x;
    };

    // Helper function to create nodes and edges
    const processOrganization = (
      org: any,
      level: number = 0,
      parentId?: string
    ) => {
      const position = nodePositions.get(org.id) || { x: 0, y: 0 };
      const isExpanded = expandedNodes.has(org.id);

      // Create node
      newNodes.push({
        id: org.id,
        type: 'organization',
        position,
        data: {
          organization: org,
          isExpanded,
          level,
          childCount: org.children?.length || 0,
          opportunityCount: org.opportunity_count || 0,
          contactCount: org.contact_count || 0,
          totalRevenue: org.total_revenue || 0,
        },
      });

      // Create edge to parent
      if (parentId) {
        newEdges.push({
          id: `${parentId}-${org.id}`,
          source: parentId,
          target: org.id,
          type: 'smoothstep',
          animated: false,
          style: {
            stroke: '#94a3b8',
            strokeWidth: 2,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 20,
            height: 20,
            color: '#94a3b8',
          },
        });
      }

      // Process children if expanded
      if (isExpanded && org.children) {
        org.children.forEach((child: any) => {
          processOrganization(child, level + 1, org.id);
        });
      }
    };

    // Calculate positions and create nodes/edges
    if (hierarchyData.length > 0) {
      calculatePosition(hierarchyData[0], 0, 0, 0);
      hierarchyData.forEach((org) => processOrganization(org, 0));
    }

    setNodes(newNodes);
    setEdges(newEdges);
  }, [hierarchyData, expandedNodes, setNodes, setEdges]);

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    event.stopPropagation();
    const nodeData = node.data as OrganizationNodeData;
    
    if (nodeData.childCount > 0) {
      setExpandedNodes((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(node.id)) {
          newSet.delete(node.id);
        } else {
          newSet.add(node.id);
        }
        return newSet;
      });
    }
  }, []);

  if (isLoading) {
    return (
      <Card className={cn("h-[600px] flex items-center justify-center", className)}>
        <div className="text-center space-y-2">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
          <p className="text-muted-foreground">Loading hierarchy...</p>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={cn("h-[600px] flex items-center justify-center", className)}>
        <div className="text-center space-y-2">
          <AlertCircle className="h-8 w-8 text-destructive mx-auto" />
          <p className="text-destructive">Failed to load hierarchy</p>
        </div>
      </Card>
    );
  }

  if (!hierarchyData || hierarchyData.length === 0) {
    return (
      <Card className={cn("h-[600px] flex items-center justify-center", className)}>
        <div className="text-center space-y-2">
          <Building className="h-8 w-8 text-muted-foreground mx-auto" />
          <p className="text-muted-foreground">No hierarchy data available</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className={cn("h-[600px] relative", className)}>
      <div className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur rounded-lg p-3 shadow-md">
        <h3 className="font-semibold text-sm mb-2">Organization Hierarchy</h3>
        <div className="space-y-1 text-xs text-muted-foreground">
          <p>• Click nodes to view details</p>
          <p>• Click nodes with children to expand/collapse</p>
          <p>• Drag to pan, scroll to zoom</p>
        </div>
      </div>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        nodeTypes={nodeTypes}
        connectionMode={ConnectionMode.Loose}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        className="bg-gray-50"
      >
        <Background 
          variant={BackgroundVariant.Dots} 
          gap={20} 
          size={1}
          color="#e2e8f0"
        />
        <Controls />
        <MiniMap 
          nodeStrokeColor={(n) => '#94a3b8'}
          nodeColor={(n) => '#ffffff'}
          nodeBorderRadius={8}
          maskColor="rgb(241, 245, 249, 0.8)"
          pannable
          zoomable
        />
      </ReactFlow>
    </Card>
  );
}

export function VisualOrganizationHierarchy(props: VisualOrganizationHierarchyProps) {
  return (
    <ReactFlowProvider>
      <VisualOrganizationHierarchyContent {...props} />
    </ReactFlowProvider>
  );
}